import { t } from 'i18next'
import { FilterMatchMode } from 'primereact/api'
import { Button } from 'primereact/button'
import {
  Column,
  ColumnEditorOptions,
  ColumnFilterElementTemplateOptions,
} from 'primereact/column'
import { ConfirmDialog } from 'primereact/confirmdialog'
import { DataTable } from 'primereact/datatable'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { Toast } from 'primereact/toast'
import React, { useState } from 'react'
import { MAX_DOSSIER_COMMENT_LENGTH } from './Helpers'
import { claims } from '../../../claims'
import { API_DOSSIER } from '../../../constants'
import { DossierDb, MokaiDocumentType } from '../../../model/dossier.model'
import { RequestMethod } from '../../../model/enums/enums'
import { SignedUrlResponse } from '../../../model/signedUrlResponse.model'
import { useAppSelector } from '../../../redux/hooks'
import { handleRequest } from '../../../services/api/base/handleRequest'
import { findPersonById } from '../../../util/arraySearch'
import { hasRequiredClaim } from '../../../util/validation/hasClaim'
import { AvatarWithName } from '../../util/AvatarWithName'
import { dateBody } from '../../util/data-table/cell-body/BirthdayBody'
import { AvatarCell } from '../../util/data-table/cell-body/FullNameApprenticeBody'
import { TextBodyWithTooltip } from '../../util/data-table/cell-body/TextBodyWithTooltip'
import { AllowPopupDialog } from '../../util/dialog/AllowPopupDialog'
import { FileTypeIcon } from '../../util/icon/FileTypeIcon'
import {
  sortByPersonNameFromInnerPersonId,
  sortDocumentTypes,
} from '../../util/sort-functions/TableSortFunctions'

/** This hook contains an editable table to display dossier files, given an array of documents */
export const DossierDataTable = (props: {
  // should never be undefined
  currentDossier: DossierDb[] // the data to be displayed in the table
  documentTypes: MokaiDocumentType[] // the MokaiDocumentTypes currently supported
  toastRef: React.RefObject<Toast> // toast reference to display messages
  scrollHeight: string // scroll height of the table
  showPerson: boolean // whether or not to show a column of the person holding the documents
  sizeFooter: () => string | JSX.Element // what to display in the footer of the table
  setCurrentDossier: (arg0: DossierDb[]) => void // callback to set the current dossier on changes of the dossier
  // currently only for deletes, arg0: the new dossiers to be displayed
  onRowEditComplete: (arg0: any) => Promise<void> // callback for edits of rows (i.e comment changes)
  // arg0: the argument given by the row editor
}): JSX.Element => {
  const [showPopupBlocked, setShowPopupBlocked] = useState<boolean>(false)

  const apprentices = useAppSelector((state) => state.apprentices)
  const employees = useAppSelector((state) => state.employees)
  const user = useAppSelector((state) => state.user)
  const userClaims = useAppSelector((state) => state.claims)

  const [fileToDelete, setFileToDelete] = useState<DossierDb>()
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)

  // we never change the filter state
  // todo is this correct?
  const [filterState, setFilterState] = useState({
    filename: { value: null, matchMode: FilterMatchMode.CONTAINS },
    comment: { value: null, matchMode: FilterMatchMode.CONTAINS },
    id_documentType: { value: null, matchMode: FilterMatchMode.EQUALS },
    id_creator: { value: null, matchMode: FilterMatchMode.CONTAINS },
    id_person: { value: null, matchMode: FilterMatchMode.EQUALS },
  })

  const getAvatarWithName = (person_id: number) => {
    let p = findPersonById(person_id, apprentices)
    if (!p) {
      p = findPersonById(person_id, employees)
    }
    return AvatarCell(p)
  }

  const onRowEditComplete = async (e: any) => {
    await props.onRowEditComplete(e)
  }

  // do we want x2 here?
  const getFileTypeIcon = (filename: string) => {
    const spl = filename.split('.')
    const ending = spl[spl.length - 1]
    return FileTypeIcon(ending)
  }

  const typeFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
    const filterValues = props.documentTypes.map((e: MokaiDocumentType) => {
      return {
        label: t(`dossier.document_types.${e.document_type_id}`),
        value: e.document_type_id,
      }
    })
    return (
      <Dropdown
        value={options.value}
        options={filterValues}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder={t<string>('core.all')}
        filter
        showFilterClear={true}
        style={{
          height: 40,
          minHeight: 40,
          width: '100%',
          minWidth: '100%',
          maxWidth: 40,
        }}
        className="p-column-filter"
      />
    )
  }

  const typeEditor = (options: ColumnEditorOptions): JSX.Element => {
    const filterValues = props.documentTypes.map((e) => {
      return {
        label: e.document_type_name,
        value: e.document_type_id,
      }
    })
    return (
      <Dropdown
        value={options.value}
        options={filterValues}
        onChange={(e) => {
          options.editorCallback?.(e.value)
        }}
        filter
      />
    )
  }

  const textEditor = (options: ColumnEditorOptions): JSX.Element => {
    return (
      <InputText
        type="text"
        value={options.value}
        maxLength={MAX_DOSSIER_COMMENT_LENGTH}
        onChange={(e) => {
          options.editorCallback?.(e.target.value)
        }}
      />
    )
  }

  const creatorFilterTemplate = (
    options: ColumnFilterElementTemplateOptions
  ) => {
    let filterValues = employees.map((e) => {
      return {
        label: e.full_name,
        value: e.person_id,
      }
    })

    return (
      <Dropdown
        value={options.value}
        options={filterValues}
        valueTemplate={(_) => personOptionFilterTemplate(options.value)}
        itemTemplate={(e) => personOptionFilterTemplate(e.value)}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder={t('core.all')}
        filter
        style={{
          height: 40,
          minHeight: 40,
          width: '100%',
          minWidth: '100%',
          maxWidth: 40,
        }}
        className="p-column-filter"
      />
    )
  }

  const ownerFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
    let filterValues = apprentices.concat(employees).map((e) => {
      return {
        label: e.full_name,
        value: e.person_id,
      }
    })

    return (
      <Dropdown
        value={options.value}
        options={filterValues}
        valueTemplate={(_) => personOptionFilterTemplate(options.value)}
        itemTemplate={(e) => personOptionFilterTemplate(e.value)}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder={t('core.all')}
        filter
        style={{
          height: 40,
          minHeight: 40,
          width: '100%',
          minWidth: '100%',
          maxWidth: 40,
        }}
        className="p-column-filter"
      />
    )
  }

  const personOptionFilterTemplate = (id: number) => {
    let p: any = findPersonById(id, employees)
    if (!p) {
      p = findPersonById(id, apprentices)
    }
    if (!p) {
      return t<string>('core.all')
    }
    return <AvatarWithName person={p} />
  }

  // function to download a given document from the bucket
  const downloadDocument = async (dossier_file_id: number) => {
    const downloadLinkResult = await handleRequest<SignedUrlResponse>(
      API_DOSSIER(dossier_file_id),
      RequestMethod.GET
    )

    const signedUrl = downloadLinkResult?.data?.url
    if (signedUrl) {
      const newWindow: Window | null = window.open(signedUrl, '_blank')
      if (newWindow) {
        newWindow.location = signedUrl
        newWindow.opener = null
        return
      } else {
        console.warn('Popup blocked!')
        setShowPopupBlocked(true)
      }
    }
  }

  const confirmDelete = (currentDossierFile: DossierDb) => {
    setFileToDelete(currentDossierFile)
    setOpenDeleteDialog(true)
  }

  const resetDeleteDialog = () => {
    setFileToDelete(undefined)
    setOpenDeleteDialog(false)
  }

  const rowEditorTemplate = (currentDossierFile: DossierDb, rowProps: any) => {
    const rowEditor = rowProps.rowEditor
    if (rowEditor.editing) {
      return rowEditor.element // default element
    } else {
      // custom init element
      // only enable deleting files that have been uploaded by the user logged in
      // or if the files belong to this person (apprentice)
      return (
        <div className={''}>
          {!hasRequiredClaim(userClaims, claims.documents.dossier.download) || (
            <Button
              className="p-button-text icon-orange"
              icon="fa-solid fa-file-arrow-down fa-lg"
              tooltip={t('core.download')}
              type={'button'}
              tooltipOptions={{ position: 'left', showDelay: 500 }}
              onClick={(_) =>
                downloadDocument(currentDossierFile.dossier_file_id)
              }
            ></Button>
          )}

          {!hasRequiredClaim(userClaims, claims.documents.dossier.edit) || (
            <Button
              type="button"
              onClick={rowEditor.onInitClick}
              tooltip={t('dossier.tooltip_edit')}
              tooltipOptions={{ position: 'left', showDelay: 500 }}
              className={rowEditor.initClassName}
            >
              <span className="p-row-editor-init-icon fa-solid fa-pen icon-blue p-clickable" />
            </Button>
          )}
          {!hasRequiredClaim(userClaims, claims.documents.dossier.delete) || (
            <Button
              icon="fas fa-trash"
              className="p-button-danger p-button-text ml-auto"
              tooltip={t('core.delete')}
              type={'button'}
              tooltipOptions={{ position: 'left', showDelay: 500 }}
              onClick={(_) => confirmDelete(currentDossierFile)}
              disabled={
                apprentices.find((e) => e.person_id === user.personId) &&
                user.personId !== currentDossierFile.id_creator
              }
            />
          )}
        </div>
      )
    }
  }

  const deleteDocument = async () => {
    if (fileToDelete) {
      const result = await handleRequest<void>(
        API_DOSSIER(fileToDelete?.dossier_file_id),
        RequestMethod.DELETE
      )
      if (result.error) {
        props.toastRef.current?.show({
          closable: true,
          severity: 'error',
          summary: t('core.action.delete.error.title'),
          detail: t('core.action.delete.error.message'),
          life: 12000,
        })
        console.warn(
          '[Dossier] Error occurred on deleting the file.',
          result.error
        )
      }
    }
    const currentStateCopy = [...props.currentDossier]
    const filteredState = currentStateCopy?.filter(
      (e) => e.dossier_file_id !== fileToDelete?.dossier_file_id
    )
    props.toastRef.current?.show({
      closable: true,
      severity: 'success',
      summary: t('core.action.delete.confirmed.title'),
      detail: t('core.action.delete.confirmed.message'),
      life: 8000,
    })
    props.setCurrentDossier(filteredState)
    resetDeleteDialog()
  }

  return (
    <>
      <DataTable
        value={props.currentDossier}
        scrollHeight={props.scrollHeight}
        rowHover={true}
        stripedRows={true}
        resizableColumns
        filters={filterState}
        filterDisplay="row"
        footer={props.sizeFooter()}
        editMode="row"
        removableSort
        onRowEditComplete={onRowEditComplete}
        className="footer justify-content-end"
      >
        <Column
          field="filename"
          body={(e) => getFileTypeIcon(e.filename)}
          dataType={'text'}
          editor={(e) => getFileTypeIcon(e.value)}
        />

        <Column
          header={t('core.file_name')}
          field={'filename'}
          sortable
          filter
          dataType={'text'}
          showFilterMenu={false}
          className={'min-width-220'}
          filterPlaceholder={t('dossier.filter')}
          body={(e) => e.filename}
        />

        <Column
          header={t<string>('dossier.document_type')}
          field={'id_documentType'}
          filter
          sortable
          sortFunction={(e) => sortDocumentTypes(e, props.documentTypes)}
          dataType={'numeric'}
          showFilterMenu={false}
          filterElement={(e) => typeFilterTemplate(e)}
          className={'min-width-220'}
          body={(e) => t(`dossier.document_types.${e.id_documentType}`)}
          editor={(e) => typeEditor(e)}
        />
        <Column
          header={t('dossier.comment')}
          field="comment"
          filter
          showFilterMenu={false}
          body={(e) => TextBodyWithTooltip(e.comment, 30)}
          dataType={'text'}
          filterPlaceholder={t('core.filter')}
          editor={(e) => textEditor(e)}
        />
        {props.showPerson && (
          <Column
            header={t('dossier.owner')}
            field={'id_person'}
            sortable
            sortFunction={(e) =>
              sortByPersonNameFromInnerPersonId(
                e,
                apprentices.concat(employees)
              )
            }
            dataType={'numeric'}
            filter
            showFilterMenu={false}
            body={(e) => getAvatarWithName(e.id_person)}
            filterElement={(e) => ownerFilterTemplate(e)}
            editor={(e) => getAvatarWithName(e.value)}
          ></Column>
        )}
        <Column
          header={t('dossier.created_on')}
          field={'created_on'}
          sortable
          dataType={'date'}
          bodyClassName={'small-text secondary-text'}
          editor={(e) => dateBody(e.value)}
          body={(e) => dateBody(e.created_on)}
        />

        <Column
          header={t<string>('core.file_creator')}
          field={'id_creator'}
          filter
          sortable
          dataType={'numeric'}
          showFilterMenu={false}
          filterElement={(e) => creatorFilterTemplate(e)}
          body={(e) => getAvatarWithName(e.id_creator)}
          editor={(e) => getAvatarWithName(e.value)}
          sortFunction={(e) =>
            sortByPersonNameFromInnerPersonId(e, apprentices.concat(employees))
          }
        />

        <Column
          header={t('dossier.size')}
          field={'size'}
          dataType={'numeric'}
          sortable
          body={(e) => `${(e.size / 1e3).toFixed(2)}kB`}
          editor={(e) => `${(e.value / 1e3).toFixed(2)}kB`}
          resizeable={false}
        />
        <Column
          rowEditor
          body={rowEditorTemplate}
          style={{
            textAlign: 'center',
            width: '120px',
            minWidth: '120px',
            maxWidth: '120px',
          }}
        />
      </DataTable>

      <ConfirmDialog
        visible={openDeleteDialog}
        header={t('dossier.delete_file.title')}
        message={t('dossier.delete_file.message', {
          name: fileToDelete?.filename,
        })}
        onHide={resetDeleteDialog}
        reject={resetDeleteDialog}
        accept={deleteDocument}
      />
      <AllowPopupDialog
        closeDialog={() => setShowPopupBlocked(false)}
        showDialog={showPopupBlocked}
      />
    </>
  )
}